/**
 * 프로젝트 캐시 작업
 */

import { getRecord, putRecord, deleteRecord, getAllRecords, clearStore } from "./index";
import type { CachedProject } from "./types";

const STORE_NAME = "projects";

/**
 * 프로젝트를 IndexedDB에 캐시
 */
export async function cacheProject(
  project: Omit<CachedProject, "cachedAt"> & { cachedAt?: number }
): Promise<void> {
  const cachedProject: CachedProject = {
    ...project,
    cachedAt: project.cachedAt ?? Date.now(),
    synced: project.synced ?? true,
  };
  await putRecord(STORE_NAME, cachedProject);
}

/**
 * 여러 프로젝트를 한 번에 캐시
 * 새 데이터가 있으면 사용, 없으면 기존 캐시 값 보존
 * (목록 쿼리에 없는 상세 필드들: address, memo, witness, equipmentStatus 등)
 */
export async function cacheProjects(
  projects: Array<Omit<CachedProject, "cachedAt" | "synced">>,
  existingCache?: Map<string, CachedProject>
): Promise<void> {
  const now = Date.now();
  for (const project of projects) {
    const existing = existingCache?.get(project._id);
    await cacheProject({
      ...project,
      // 기존 캐시에서 상세 필드들 보존 (목록 쿼리에 없는 필드들)
      type: project.type ?? existing?.type,
      address: project.address ?? existing?.address,
      memo: project.memo ?? existing?.memo,
      witness: project.witness ?? existing?.witness,
      equipmentStatus: project.equipmentStatus ?? existing?.equipmentStatus,
      cachedAt: now,
      synced: true,
    });
  }
}

/**
 * 서버 데이터로 캐시 동기화 (삭제된 프로젝트 제거)
 * 서버에서 받은 프로젝트 목록과 비교하여 캐시를 업데이트
 * 기존 캐시의 equipmentStatus는 보존합니다.
 */
export async function syncCacheWithServer(
  serverProjects: Array<Omit<CachedProject, "cachedAt" | "synced">>
): Promise<void> {
  // 기존 캐시된 프로젝트 목록 (equipmentStatus 보존을 위해 Map으로 변환)
  const cachedProjects = await getAllRecords<CachedProject>(STORE_NAME);
  const cachedMap = new Map(cachedProjects.map((p) => [p._id, p]));

  // 서버 프로젝트 ID 목록
  const serverIds = new Set(serverProjects.map((p) => p._id));

  // 서버에 없는 프로젝트는 캐시에서 삭제 (삭제된 프로젝트)
  for (const [cachedId] of cachedMap) {
    if (!serverIds.has(cachedId)) {
      await deleteRecord(STORE_NAME, cachedId);
    }
  }

  // 서버 프로젝트를 캐시에 저장/업데이트 (기존 equipmentStatus 보존)
  await cacheProjects(serverProjects, cachedMap);
}

/**
 * ID로 캐시된 프로젝트 조회
 */
export async function getCachedProject(id: string): Promise<CachedProject | undefined> {
  return getRecord<CachedProject>(STORE_NAME, id);
}

/**
 * 모든 캐시된 프로젝트 조회 (최신순 정렬)
 */
export async function getCachedProjects(): Promise<CachedProject[]> {
  const projects = await getAllRecords<CachedProject>(STORE_NAME);
  // createdAt 기준 내림차순 정렬 (최신순)
  return projects.sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });
}

/**
 * 캐시된 프로젝트 삭제
 */
export async function removeCachedProject(id: string): Promise<void> {
  await deleteRecord(STORE_NAME, id);
}

/**
 * 모든 프로젝트 캐시 삭제
 */
export async function clearProjectCache(): Promise<void> {
  await clearStore(STORE_NAME);
}

/**
 * 프로젝트 로컬 변경사항 저장 (오프라인 수정)
 */
export async function updateCachedProject(
  id: string,
  changes: Partial<CachedProject>
): Promise<void> {
  const existing = await getCachedProject(id);
  if (existing) {
    await cacheProject({
      ...existing,
      ...changes,
      synced: false,
      cachedAt: Date.now(),
    });
  }
}
